/**
 * Вхід і реєстрація passkey (Face ID / відбиток / апаратний ключ).
 *
 * Уся бінарна кухня WebAuthn — base64url, ArrayBuffer, transports — лежить у
 * @simplewebauthn/browser і навмисно не переписується тут руками: саме на
 * ручних перетвореннях легко розійтися з сервером, який розбирає відповідь
 * парною бібліотекою @simplewebauthn/server.
 *
 * Ключі РЕЗИДЕНТНІ: сервер не питає пошти й не віддає список ключів, тому
 * запит на вхід іде без жодного ідентифікатора — браузер сам пропонує
 * підхожий ключ, а кому він належить, сервер дізнається з підпису.
 */

import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { zMeResponse, zPasskeyList, type MeResponse, type PasskeyList } from '@lk/shared';
import { apiRequest } from '../../lib/http';
import { isLoginFailed } from '../../lib/errors';
import { ENDPOINTS } from '../../lib/api';
import {
  forgetLocalPasskey,
  getDeviceId,
  hasLocalPasskey,
  rememberLocalPasskey,
} from '../../lib/device';

/** Помилка, яку вже можна показати людині як є. */
export class PasskeyError extends Error {
  override readonly name = 'PasskeyError';
  /** true — на цьому пристрої ключа немає, кнопку входу треба сховати. */
  readonly missingKey: boolean;

  constructor(message: string, opts: { missingKey?: boolean; cause?: unknown } = {}) {
    super(message);
    this.missingKey = opts.missingKey ?? false;
    if (opts.cause !== undefined) this.cause = opts.cause;
  }
}

/**
 * Перетворює відмову автентифікатора в людський текст.
 *
 * Окремий випадок, який тут головний: `NotAllowedError` браузер видає і коли
 * користувач натиснув «Скасувати», і коли підхожого ключа просто немає —
 * розрізнити їх зовні неможливо (це зроблено навмисно, щоб сайт не міг
 * промацувати наявність ключів). Тому текст говорить про обидва варіанти.
 */
function describe(error: unknown, flow: 'login' | 'register'): PasskeyError {
  if (error instanceof PasskeyError) return error;

  if (error instanceof WebAuthnError) {
    switch (error.code) {
      case 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED':
        return new PasskeyError('Цей пристрій уже додано — новий ключ не потрібен.', { cause: error });
      case 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT':
        return new PasskeyError('Пристрій не вміє зберігати ключ у собі — швидкий вхід тут не запрацює.', {
          cause: error,
        });
      case 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT':
        return new PasskeyError('На пристрої не налаштовані відбиток, Face ID або код блокування.', {
          cause: error,
        });
      case 'ERROR_INVALID_DOMAIN':
      case 'ERROR_INVALID_RP_ID':
        return new PasskeyError('Ключі працюють лише на основному домені застосунку.', { cause: error });
      default:
        break;
    }
  }

  const name = error instanceof Error ? error.name : '';
  const domName = error instanceof Error && error.cause instanceof Error ? error.cause.name : '';

  if (name === 'NotAllowedError' || domName === 'NotAllowedError' || name === 'AbortError') {
    return flow === 'login'
      ? new PasskeyError('Не вдалося підтвердити — або скасовано, або на цьому пристрої немає ключа.', {
          missingKey: true,
          cause: error,
        })
      : new PasskeyError('Додавання ключа скасовано.', { cause: error });
  }

  if (name === 'InvalidStateError' || domName === 'InvalidStateError') {
    return new PasskeyError('Цей пристрій уже додано — новий ключ не потрібен.', { cause: error });
  }

  const message = error instanceof Error && error.message ? error.message : 'Автентифікатор не відповів.';
  return new PasskeyError(message, { cause: error });
}

/** Чи є на пристрої вбудований автентифікатор (Face ID / Touch ID / Windows Hello). */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * Чи показувати кнопку «Face ID» на екрані входу.
 * Тільки там, де ключ реально додавали: див. `hasLocalPasskey`.
 */
export function canLoginWithPasskey(): boolean {
  return browserSupportsWebAuthn() && hasLocalPasskey();
}

export function fetchPasskeys(signal?: AbortSignal): Promise<PasskeyList> {
  return apiRequest(ENDPOINTS.passkeys, {
    schema: zPasskeyList,
    ...(signal ? { signal } : {}),
  });
}

export function deletePasskey(id: string): Promise<PasskeyList> {
  return apiRequest(ENDPOINTS.passkey(id), { method: 'DELETE', schema: zPasskeyList });
}

/** Додати ключ на цьому пристрої. Викликається з кабінету батьків. */
export async function registerPasskey(): Promise<PasskeyList> {
  const options = await apiRequest<PublicKeyCredentialCreationOptionsJSON>(
    ENDPOINTS.passkeyRegisterOptions,
    { method: 'POST', body: {} },
  );

  const attestation = await startRegistration({ optionsJSON: options }).catch((error: unknown) => {
    throw describe(error, 'register');
  });

  const list = await apiRequest(ENDPOINTS.passkeyRegisterVerify, {
    method: 'POST',
    body: { response: attestation },
    schema: zPasskeyList,
  });

  rememberLocalPasskey();
  return list;
}

/** Вхід за passkey. Поруч завжди лишається вхід за PIN. */
export async function loginWithPasskey(): Promise<MeResponse> {
  const options = await apiRequest<PublicKeyCredentialRequestOptionsJSON>(ENDPOINTS.passkeyLoginOptions, {
    method: 'POST',
    body: {},
    silentUnauthorized: true,
  });

  const assertion = await startAuthentication({ optionsJSON: options }).catch((error: unknown) => {
    const described = describe(error, 'login');
    // Ключа на пристрої немає — прибираємо прапорець, щоб кнопка не
    // висіла на екрані входу й не збивала дитину.
    if (described.missingKey) forgetLocalPasskey();
    throw described;
  });

  const me = await apiRequest(ENDPOINTS.passkeyLoginVerify, {
    method: 'POST',
    schema: zMeResponse,
    silentUnauthorized: true,
    body: { response: assertion, deviceId: getDeviceId() },
  }).catch((error: unknown) => {
    /**
     * Сервер ключа не прийняв. Це не «сесія протухла», тому й текст інший:
     * людині треба знати, що вихід є — PIN нікуди не подівся.
     *
     * Прапорець `missingKey` тут НЕ ставимо: серед причин є цілком поправні
     * (протухлий виклик), і зникла кнопка змусила б заново додавати живий
     * ключ там, де досить повторити спробу.
     */
    if (isLoginFailed(error)) {
      throw new PasskeyError('Ключ не підійшов. Спробуй ще раз або зайди за PIN.', { cause: error });
    }
    throw error;
  });

  rememberLocalPasskey();
  return me;
}
